import siteOperationTypesStyles from '../site-workspace/site-operation-types-section.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { SecurityApi } from '../../../api/security/security-api';
import { ApiSiteMaintenance } from '../../../api/core/api-site-maintenance';
import { toErrorMessage } from '../../../api/error-utils';
import type { SecurityDomainDto } from '../../../api/agwa-api-model';
import { terms } from '../../../language/terms';
import {
  getOrganizationTypeDescription,
  getOrganizationTypeLabel,
} from '../shared/organization-type-labels';
import { SHOW_UNASSIGNABLE_OPERATION_TYPES } from '../shared/operation-type-visibility';

/**
 * The company's operation types as a READONLY reflection of its sites.
 *
 * Since operation types became site-scoped end to end, a company IS whatever its sites are -
 * there is nothing to register at this level, and the company-level management surfaces are
 * hidden (not deleted) pending any reintroduction. The architect's ruling (2026-09-05): show
 * the company's types "in the same format as the site does, but... a readonly reflection of
 * the sites that they inherit from". Same rows, same codes-then-names, same tooltip - the two
 * surfaces stay one vocabulary - but the checkboxes state facts nobody can flip here, and the
 * card says where to go to change them.
 *
 * Data is fetched, not passed (component portability): the full catalogue from SecurityApi so
 * unregistered types still render as unticked rows, and the group's sites from
 * ApiSiteMaintenance - each row's organizationTypes already carries only ACTIVE scopes, so the
 * union here inherits that meaning.
 */
@customElement('company-operation-types-section')
export class CompanyOperationTypesSection extends LitElement {
  static styles = [sharedStyles, unsafeCSS(siteOperationTypesStyles)];

  @property({ type: String }) groupId = '';

  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private domains: SecurityDomainDto[] = [];
  /** Union of the sites' active operation-type codes, keyed for the tick state. */
  @state() private inheritedCodes = new Set<string>();

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('groupId')) {
      if (this.groupId) {
        void this.loadData();
      } else {
        this.domains = [];
        this.inheritedCodes = new Set();
      }
    }
  }

  private async loadData() {
    if (!this.groupId) return;
    this.loading = true;
    this.error = null;
    try {
      const securityApi = ApiFactory.authenticated(SecurityApi);
      const siteApi = ApiFactory.authenticated(ApiSiteMaintenance);
      const [domainsResp, sitesResp] = await Promise.all([
        securityApi.getSecurityDomains(),
        siteApi.getMemberSites(this.groupId),
      ]);
      if (!domainsResp.success) throw new Error(toErrorMessage(domainsResp));
      if (!sitesResp.success) throw new Error(toErrorMessage(sitesResp));

      this.domains = domainsResp.data?.domains ?? [];
      const codes = new Set<string>();
      for (const site of sitesResp.data?.items ?? []) {
        for (const code of site.organizationTypes ?? []) {
          codes.add(code);
        }
      }
      this.inheritedCodes = codes;
    } catch (error) {
      this.error =
        (error as Error)?.message ??
        `Unable to load ${terms.userGroupDomain.lowerPlural}.`;
    } finally {
      this.loading = false;
    }
  }

  /** Same filter as the site section, so the two lists can never disagree on membership. */
  private get listedDomains(): SecurityDomainDto[] {
    return this.domains.filter((domain) => {
      if (!domain.code) return false;
      if (domain.scopeLevel !== 'Site') return false;
      if (domain.uiAssignable === false) {
        return SHOW_UNASSIGNABLE_OPERATION_TYPES;
      }
      return true;
    });
  }

  private renderRow(domain: SecurityDomainDto) {
    const code = domain.code;
    const inherited = this.inheritedCodes.has(code);
    const label = getOrganizationTypeLabel(code);
    const description = getOrganizationTypeDescription(code);

    return html`
      <div class="operation-type-row ${inherited ? 'is-registered' : ''}">
        <div class="operation-type-head">
          <!-- Disabled is the honesty here: the tick is a fact about the sites, and the only
               place it can be changed is on a site. -->
          <!-- disabled carries the semantics (nothing here can be flipped); the shared
               sheet's readonly-fact override strips the greyed-out paint, because a FACT is
               not a disabled control - the architect read the fade as wrongness, not
               readonlyness (2026-09-05). -->
          <sl-checkbox
            class="operation-type-name readonly-fact"
            ?checked=${inherited}
            disabled
          >
            ${
              domain.shortCode
                ? html`<span class="operation-type-code"
                    >${domain.shortCode}</span
                  >`
                : nothing
            }${label}</sl-checkbox
          >
          ${
            description
              ? html`
                  <sl-tooltip content=${description}>
                    <sl-icon
                      class="operation-type-info"
                      name="info-circle"
                      label="About ${label}"
                    ></sl-icon>
                  </sl-tooltip>
                `
              : nothing
          }
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading && this.domains.length === 0) {
      return html`
        <div class="loading-state">
          <sl-spinner></sl-spinner>
          <span>Loading ${terms.userGroupDomain.lowerPlural}...</span>
        </div>
      `;
    }

    if (this.error) {
      return html`<sl-alert variant="danger" open>${this.error}</sl-alert>`;
    }

    const listed = this.listedDomains;
    if (listed.length === 0) {
      return html`
        <div class="empty-state">
          <span>No ${terms.userGroupDomain.lowerPlural} are available.</span>
        </div>
      `;
    }

    return html`
      <p class="operation-types-inherited-note">
        Reflected from this ${terms.loginGroup.lower}'s
        ${terms.loginSite.lowerPlural}. Register or change
        ${terms.userGroupDomain.lowerPlural} on each ${terms.loginSite.lower}.
      </p>
      <div class="operation-types">
        ${listed.map((domain) => this.renderRow(domain))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'company-operation-types-section': CompanyOperationTypesSection;
  }
}
